import { useCallback, useEffect, useState } from "react";

import {
  type DashboardOrganizationApiKey,
  listOrganizationApiKeysAsUser,
} from "../dashboard-client";

interface UseApiKeysState {
  keys: DashboardOrganizationApiKey[];
  loading: boolean;
  error: string | null;
}

export function useApiKeys(organizationId: string | null) {
  // Loading from the first render, so an empty list is never shown as a
  // real answer before the request starts.
  const [state, setState] = useState<UseApiKeysState>({
    error: null,
    keys: [],
    loading: organizationId !== null,
  });

  const refresh = useCallback(async () => {
    if (!organizationId) {
      setState({ error: null, keys: [], loading: false });
      return;
    }
    setState((s) => ({ ...s, error: null, loading: true }));
    try {
      const { data } = await listOrganizationApiKeysAsUser({
        path: { organizationId },
      });
      const keys = data.items;
      setState({ error: null, keys, loading: false });
    } catch (error) {
      setState((s) => ({
        ...s,
        error:
          error instanceof Error ? error.message : "Failed to load API keys",
        loading: false,
      }));
    }
  }, [organizationId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return {
    error: state.error,
    keys: state.keys,
    loading: state.loading,
    refresh,
  };
}
